/**
 * PageHeader - Unified page header component
 *
 * Provides consistent header styling with:
 * - Title and optional subtitle
 * - Icon
 * - Action buttons slot
 * - Optional breadcrumbs
 * - Optional tabs
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface TabItem {
  id: string;
  label: string;
  count?: number;
  icon?: ReactNode;
}

export interface PageHeaderProps {
  /** Page title */
  title: string;

  /** Optional subtitle or description */
  subtitle?: string;

  /** Icon displayed before title */
  icon?: ReactNode;

  /** Action buttons/controls on the right side */
  actions?: ReactNode;

  /** Breadcrumb navigation */
  breadcrumbs?: BreadcrumbItem[];

  /** Tab navigation */
  tabs?: TabItem[];

  /** Currently active tab ID */
  activeTab?: string;

  /** Callback when tab changes */
  onTabChange?: (tabId: string) => void;

  /** Background color (default: white) */
  background?: string;

  /** Border style (default: border-b) */
  borderStyle?: 'none' | 'bottom' | 'shadow';

  /** Additional className */
  className?: string;

  /** Compact mode with reduced padding */
  compact?: boolean;
}

export function PageHeader({
  title,
  subtitle,
  icon,
  actions,
  breadcrumbs,
  tabs,
  activeTab,
  onTabChange,
  background = 'white',
  borderStyle = 'bottom',
  className = '',
  compact = false,
}: PageHeaderProps) {
  const borderClasses = {
    none: '',
    bottom: 'border-b border-line',
    shadow: 'shadow-sm',
  };

  return (
    <header
      className={`${borderClasses[borderStyle]} ${className}`}
      style={{ backgroundColor: background }}
    >
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav
          className={`flex items-center gap-1.5 text-sm ${compact ? 'px-4 pt-3' : 'px-6 pt-4'}`}
          aria-label="Breadcrumb"
        >
          {breadcrumbs.map((crumb, index) => (
            <div key={index} className="flex items-center gap-1.5">
              {index > 0 && (
                <ChevronRight size={14} className="text-subtle" />
              )}
              {crumb.href ? (
                <Link
                  to={crumb.href}
                  className="text-muted hover:text-coral-600 transition-colors"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-text font-medium">
                  {crumb.label}
                </span>
              )}
            </div>
          ))}
        </nav>
      )}

      {/* Main header content */}
      <div className={`flex items-center justify-between ${compact ? 'px-4 py-3' : 'px-6 py-4'}`}>
        <div className="flex items-center gap-3 min-w-0">
          {icon && (
            <div className="p-2 bg-coral-100 rounded-lg flex-shrink-0">
              <span className="text-coral-600">
                {icon}
              </span>
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-text truncate">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-muted truncate mt-0.5">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {actions && (
          <div className="flex items-center gap-3 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>

      {/* Tabs */}
      {tabs && tabs.length > 0 && (
        <div className={`flex items-center gap-1 ${compact ? 'px-4 pb-0' : 'px-6 pb-0'}`}>
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange?.(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-2.5 text-sm font-medium
                  border-b-2 transition-colors -mb-px
                  ${isActive
                    ? 'border-coral-500 text-coral-600'
                    : 'border-transparent text-muted hover:text-text hover:border-line'
                  }
                `}
              >
                {tab.icon && (
                  <span className={isActive ? 'text-coral-500' : 'text-subtle'}>
                    {tab.icon}
                  </span>
                )}
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span
                    className={`
                      px-1.5 py-0.5 text-xs rounded-full
                      ${isActive
                        ? 'bg-coral-100 text-coral-600'
                        : 'bg-surface-subtle text-muted'
                      }
                    `}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </header>
  );
}

export default PageHeader;
